// Side-effect import: <std-state-dot> must be registered before this
// hydrates one, and the class is otherwise referenced only as a type.
import "../turn/state-dot";

import { LonaWebComponent, html } from "@tento-ui/component";
import { component } from "@tento-ui/component-decorators";
import { css } from "@tento-ui/component-styles";
import type { StateDot } from "../turn/state-dot";

/** `result`, in the dsh register — how the turn ended, at the weight the
 * ending deserves, which for a turn that went fine is NOTHING.
 *
 * ## ok renders zero pixels
 *
 * The same verdict the native skin reached, and it is not a house style
 * borrowed from it: a mark that appears on every turn carries no information
 * the reader did not already have from the reply being there, and once every
 * turn has one the reader stops seeing marks at all — including the one that
 * says something went wrong. The component still ACCEPTS an ok result,
 * because callers project one from the wire and should not have to branch.
 *
 * One caller does have to branch, and it is worth saying where. A host that
 * MEASURES its rows reads a height of 0 as "not yet measured", because an
 * unmounted cell reports 0 as well. A virtualized transcript skips the ok
 * rather than mounting it.
 *
 * ## error is not a disclosure row, and that is deliberate
 *
 * Every other row in this skin is one. This one is not, because a failure is
 * the one thing in a transcript a reader must not have to open — a chevron
 * over an error is a control between the reader and the fact they came back
 * for. So it is a plain three-column grid:
 *
 *     ●  something failed                    exit 101
 *        the machine's own words
 *
 * A mark, a message, and a code pinned to the end of the line. The title
 * takes `--text-pri` at 500 — the only place in this skin that reaches past
 * the two gray steps, which is what makes it findable in a column that
 * otherwise never does. The `detail` still folds, because a stack trace is
 * a body and the summary above it is already the answer for most readers;
 * the toggle is a plain word, not a chevron, so it cannot be mistaken for
 * the row grammar.
 *
 * ## suspended is a fact about the session, not a warning
 *
 * One dim line and a `warn` dot. A turn that stopped without finishing is the
 * one ending a reader cannot infer from the page — both vendor surfaces make
 * it visible and neither makes it loud. Resuming is an application decision
 * and stays with the caller. */
@component({ name: "std-dsh-turn-result" })
export class DshTurnResult extends LonaWebComponent {
  private $dot: StateDot = this.hydrate("dot");
  private $title: HTMLElement = this.$("title");
  private $message: HTMLElement = this.$("message");
  private $code: HTMLElement = this.$("code");
  private $toggle: HTMLButtonElement = this.$<HTMLButtonElement>("toggle");
  private $detail: HTMLElement = this.$("detail");

  /** Whether the folded body is showing. Written on EVERY bind from the
   * caller's `expanded`, never accumulated — a pooled result the reader had
   * opened must come back closed, holding the new turn's detail. */
  private open = false;

  static makeWith(props: DshTurnResult.Props): DshTurnResult {
    return (DshTurnResult.make() as DshTurnResult).bind(props);
  }

  bind(props: DshTurnResult.Props): this {
    this.setAttribute("status", props.status);
    // Bound on EVERY path, including `ok`, which draws nothing. The dot is a
    // pooled child of a pooled row; an ok result recycled out of an error one
    // would otherwise keep the red state, invisible today and visible the
    // moment a CSS rule changes.
    void this.$dot.bind({ state: props.status === "error" ? "error" : "warn" });

    const head = DshTurnResult.split(props.text);
    this.$title.textContent = head.title;
    this.$code.textContent = head.code;
    this.$code.style.display = head.code === "" ? "none" : "";
    this.$message.textContent = head.message;
    this.$message.style.display = head.message === "" ? "none" : "";

    const detail = props.detail ?? "";
    this.$detail.textContent = detail;
    this.$toggle.style.display = detail === "" ? "none" : "";
    this.open = detail !== "" && (props.expanded ?? false);
    // ASSIGNED rather than added, and assigned in bind rather than on
    // connect. `addEventListener` on a pooled element accumulates one
    // handler per bind, so the third click would toggle three times; and a
    // `connectedCallback` wiring never runs for an element built, bound and
    // measured before it is inserted. One property, written every bind.
    this.$toggle.onclick = () => {
      this.open = !this.open;
      this.renderDetail();
    };
    this.renderDetail();
    return this;
  }

  private renderDetail(): void {
    this.$detail.style.display = this.open ? "" : "none";
    this.$toggle.textContent = this.open
      ? DshTurnResult.HIDE_DETAIL
      : DshTurnResult.SHOW_DETAIL;
  }

  /** The one line, split into the three things the grid draws.
   *
   * The separator is `·`, which is this family's own: the native tool row
   * already writes `Ran cargo build · exit 0`, so a producer that phrases a
   * failure the same way gets its code in the code column for free and one
   * that does not gets a title and an empty column. The LAST separator wins,
   * because a message may legitimately contain one and the code never does.
   *
   * Lines past the first are the machine's own words, which sit under the
   * title in the secondary step. Nothing here parses; it splits. */
  private static split(text: string): {
    title: string;
    code: string;
    message: string;
  } {
    const lines = text.split("\n");
    const head = lines[0] ?? "";
    const message = lines.slice(1).join("\n").trim();
    const at = head.lastIndexOf(" · ");
    if (at < 0) return { title: head, code: "", message };
    return {
      title: head.slice(0, at),
      code: head.slice(at + 3),
      message,
    };
  }

  static $styles = [
    css`
      :host {
        display: block;
        width: 100%;
        min-width: 0;
      }
      /* A turn that went fine is unmarked. Zero pixels, not a quiet line. */
      :host([status="ok"]) {
        display: none;
      }
      /* 10px for the mark, everything for the words, and the code takes
         exactly its own width at the end. Not a flex row: the message and
         the folded body have to hang under the TITLE rather than under the
         mark, and a grid says that with a column rather than with an indent
         that has to be kept equal to the mark by hand. */
      #root {
        display: grid;
        grid-template-columns: 10px minmax(0, 1fr) auto;
        column-gap: 8px;
        align-items: baseline;
        min-height: 20px;
      }
      #dot {
        /* The mark is the circle face here, which has no pixel grid to
           miss — see the note in std-state-dot on why 14 is its default.
           10px is the reviewed weight against this family's words and it is
           what makes the first column the same 10px every row in the native
           skin spends on a status. */
        --state-dot-size: 10px;
        align-self: center;
      }
      #main {
        min-width: 0;
        display: flex;
        flex-direction: column;
        gap: 2px;
      }
      /* The one place this skin reaches past its two gray steps. Everything
         else is --text-sec against --text-ter; a failure is the row the
         reader is scanning for, so it gets the primary tone and a half step
         of weight — and nothing else does, which is what keeps it findable. */
      #title {
        margin: 0;
        --font-size: var(--small-size);
        color: var(--text-pri, #1f2328);
        font-weight: 500;
        overflow-wrap: anywhere;
      }
      #message {
        margin: 0;
        --font-size: var(--small-size);
        color: var(--text-sec, #6a7186);
        white-space: pre-wrap;
        overflow-wrap: anywhere;
      }
      /* Mono, dim, and at the end of the line: a code is scanned character
         by character and compared against another one, which is the only
         thing monospace is for on this surface. */
      #code {
        margin: 0;
        --font-size: var(--turn-fine-size, var(--text-8px));
        font-family: var(--font-mono, ui-monospace, SFMono-Regular, Menlo, monospace);
        color: var(--text-ter, #8b93a7);
        white-space: nowrap;
        opacity: 0.85;
      }
      /* A word, not a chevron. Every chevron in this skin belongs to the row
         grammar, and this is not a row — borrowing its control would promise
         a disclosure row that behaves like the others and does not. */
      #toggle {
        align-self: flex-start;
        margin: 1px 0 0 0;
        padding: 0;
        border: none;
        background: none;
        font: inherit;
        --font-size: var(--small-size);
        color: var(--text-ter, #8b93a7);
        cursor: pointer;
        text-align: left;
      }
      #toggle:hover {
        color: var(--text-sec, #6a7186);
      }
      #detail {
        margin: 3px 0 0 0;
        padding: 0;
        min-width: 0;
        max-height: 220px;
        --font-size: var(--turn-fine-size, var(--text-8px));
        --line-height-ratio: 1.55;
        color: var(--text-sec, #6a7186);
        white-space: pre;
        overflow: auto;
      }

      /* ---- suspended: one dim line ------------------------------- */
      /* Not an error and drawn as not one. The title drops to the secondary
         step and loses its weight, which is the entire difference between
         "this stopped" and "this failed" — the yellow mark is already
         carrying the rest. */
      :host([status="suspended"]) #title {
        color: var(--text-sec, #6a7186);
        font-weight: 400;
      }
    `,
  ];

  static $html: Option<HTMLTemplateElement> = html`
    <div id="root">
      <std-state-dot id="dot"></std-state-dot>
      <div id="main">
        <p class="small" id="title"></p>
        <p class="small" id="message"></p>
        <button class="small" id="toggle" type="button"></button>
        <pre class="code-block" id="detail"></pre>
      </div>
      <p class="small" id="code"></p>
    </div>
  `;
}

export namespace DshTurnResult {
  export type Status = "ok" | "error" | "suspended";

  export const SHOW_DETAIL = "show details";
  export const HIDE_DETAIL = "hide details";

  export type Props = {
    status: Status;
    /** The one-line outcome. A trailing ` · code` becomes the code column;
     * lines past the first become the message under the title. Ignored when
     * `status` is "ok", which draws nothing. */
    text: string;
    /** What the reader can open — a stack, a failing assertion, a stop
     * reason. Folded behind a plain word. */
    detail?: string;
    expanded?: boolean;
  };
}
